/**
 * Our Table 12 against the client's own GSTR-1 HSN summary (W3-12 (4a), ADR-0034 D-50).
 *
 * Where the client sent the HSN summary their software printed (Tally's *GSTR-1 – HSN/SAC
 * Summary*, uploaded to **Portal GSTR-1**), ProGST still computes the return from the
 * register, and this card shows every difference — per section, code and rate, with
 * `Diff = ours − theirs`, the convention Prepared vs Filed states too.
 *
 * **GSTN's Excel layout carries the whole return** (W3-12 (4b-1), D-72), so for it the card
 * also sets every other table beside ours — what the template states for each, field by field.
 *
 * **ProGST's own utility workbook is not the client's** (W3-12 (4b-2), P5): an upload that is byte
 * for byte one ProGST generated is named as such, and never shown as an agreement.
 *
 * **It renders; it decides nothing** (ADR-0014). A difference may be a line still waiting
 * for its code, a code the client chose that GSTN does not publish, or a sale one side has
 * and the other does not. The card names which; the reviewer decides.
 */

import { Alert, Card } from '@fiveox/ui'

import type { ClientGstr1Check } from '../lib/api'
import {
  FIELD_WORDS,
  PROBLEM_WORDS,
  ROW_WORDS,
  TABLE_WORDS,
  amount,
  ownFileWords,
  rowWords,
  rupees,
  signed,
  signedAmount,
} from '../lib/clientGstr1'

const SECTION_WORDS: Record<string, string> = {
  b2b: 'B2B — registered buyers',
  b2c: 'B2C — everyone else',
  all: 'All supplies',
}

export function ClientGstr1CheckCard({ check }: { check: ClientGstr1Check | null }) {
  if (!check?.present || !check.comparison) return null
  const c = check.comparison
  const whole = c.tables !== undefined
  // P5: ProGST's own workbook and no client's file before it — nothing to compare.
  const ownOnly = c.own_file !== undefined && c.sections.length === 0 && !whole
  if (ownOnly && c.own_file) {
    return (
      <Card className="mb-4 p-4">
        <p className="gst-panel-h">Against the client&apos;s own GSTR-1</p>
        <Alert tone="warning" className="mb-0">
          {ownFileWords(c.own_file)}
        </Alert>
      </Card>
    )
  }
  return (
    <Card className="mb-4 p-4">
      <p className="gst-panel-h">
        {whole ? "Against the client's own GSTR-1" : "Against the client's own GSTR-1 HSN summary"}
      </p>
      {c.own_file && (
        <Alert tone="warning" className="mb-3">
          {ownFileWords(c.own_file)} The comparison below is with the client&apos;s earlier file.
        </Alert>
      )}
      <p className="gst-meta mb-3">
        {check.source_filename ?? 'The uploaded summary'}
        {c.period_from && c.period_to && ` · ${c.period_from} to ${c.period_to}`}
        {c.month && ` · ${c.month}`}
        {' · '}Diff = ours − the client&apos;s
      </p>
      {c.ok ? (
        <Alert tone="success" className="mb-3">
          {whole
            ? "Every table agrees with the client's GSTR-1, to the paisa."
            : "Table 12 agrees with the client's summary, code by code, to the paisa."}
        </Alert>
      ) : (
        c.waiting_on_codes > 0 && (
          <Alert tone="warning" className="mb-3">
            {c.waiting_on_codes.toLocaleString()} line
            {c.waiting_on_codes === 1 ? ' is' : 's are'} still waiting for an HSN/SAC, so Table 12
            cannot agree yet. Accept the summary&apos;s codes on Raw Data.
          </Alert>
        )
      )}
      {c.problems.length > 0 && (
        <ul className="mb-3 list-disc pl-5 text-sm">
          {c.problems.map((p, i) => (
            <li key={i}>{PROBLEM_WORDS[p.code]?.(p) ?? p.code}</li>
          ))}
        </ul>
      )}
      {c.sections.map((s) => (
        <div key={s.section} className="gst-table-wrap mb-3">
          <table className="gst-table">
            <thead>
              <tr>
                <th className="is-text" colSpan={2}>
                  {SECTION_WORDS[s.section] ?? s.section}
                </th>
                <th>Ours — taxable (₹)</th>
                <th>Client&apos;s — taxable (₹)</th>
                <th>Diff — taxable (₹)</th>
                <th>Diff — tax (₹)</th>
                <th className="is-text">Status</th>
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r, i) => (
                <tr key={`${r.hsn}-${String(r.rate)}`} className={i % 2 === 1 ? 'is-a' : ''}>
                  <td className="is-text gst-mono">{r.hsn}</td>
                  <td className="is-text">{r.rate === null ? '—' : `${r.rate}%`}</td>
                  <td>{rupees(r.ours.taxable)}</td>
                  <td>{rupees(r.theirs.taxable)}</td>
                  <td>{signed(r.diff.taxable)}</td>
                  <td>{signed(r.diff.igst + r.diff.cgst + r.diff.sgst + r.diff.cess)}</td>
                  <td className="is-text">
                    <span className={`gst-chip ${r.ok ? 'is-ok' : 'is-hold'}`}>{rowWords(r)}</span>
                  </td>
                </tr>
              ))}
              <tr className="is-net">
                <td className="is-sec" colSpan={2}>
                  TOTAL
                </td>
                <td>{rupees(s.ours.taxable)}</td>
                <td>{rupees(s.theirs.taxable)}</td>
                <td>{signed(s.diff.taxable)}</td>
                <td>{signed(s.diff.igst + s.diff.cgst + s.diff.sgst + s.diff.cess)}</td>
                <td className="is-text">
                  <span className={`gst-chip ${s.ok ? 'is-ok' : 'is-hold'}`}>
                    {s.ok ? 'Agrees' : 'Differs'}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ))}
      {whole && c.tables && c.tables.length > 0 && (
        <div className="gst-table-wrap mb-3">
          <table className="gst-table">
            <thead>
              <tr>
                <th className="is-text">The other tables</th>
                <th className="is-text">On</th>
                <th>Ours</th>
                <th>Client&apos;s</th>
                <th>Diff</th>
                <th className="is-text">Status</th>
              </tr>
            </thead>
            <tbody>
              {c.tables.flatMap((t, i) =>
                t.fields.map((field, j) => (
                  <tr key={`${t.table}-${t.row ?? ''}-${field}`} className={i % 2 === 1 ? 'is-a' : ''}>
                    <td className="is-text">
                      {j === 0 &&
                        `${TABLE_WORDS[t.table] ?? t.table}${t.row ? ` — ${ROW_WORDS[t.row] ?? t.row}` : ''}`}
                    </td>
                    <td className="is-text">{FIELD_WORDS[field] ?? field}</td>
                    <td>{amount(field, t.ours[field] ?? 0)}</td>
                    <td>{amount(field, t.theirs[field] ?? 0)}</td>
                    <td>{signedAmount(field, t.diff[field] ?? 0)}</td>
                    <td className="is-text">
                      <span className={`gst-chip ${(t.diff[field] ?? 0) === 0 ? 'is-ok' : 'is-hold'}`}>
                        {(t.diff[field] ?? 0) === 0 ? 'Agrees' : 'Differs'}
                      </span>
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}
